import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'react-string-format'
import moment from 'moment'
import { Template } from 'devextreme-react/core/template'
import DataGrid, { Column, FilterRow, SearchPanel, Paging, Pager, Scrolling, Export, Summary, TotalItem, Editing } from 'devextreme-react/data-grid'
import { ProgressBar } from '@blueprintjs/core'
/* API LIST */
import {
  getContractDetail
  // addEfficiencyLossRate
} from '../../api/contract'
/* CUSTOM HOOKS */
import useAlert from '../../lib/useAlert'
/* COMPONENTS */
import GridCell from '../../components/DevExpressGrid/GridCell'
import ToolbarExportButton from '../../components/DevExpressGrid/ToolbarExportButton'

const ContractDetail = React.forwardRef((props, ref) => {
  const { t } = useTranslation()
  const { openAlert } = useAlert()
  const [values, setValues] = useState([])
  const [rows, setRows] = useState([])

  const refGrid = useRef()

  const generateExportFileName = format(t('export_filename_contract_detail'), values.vbeln, moment().format('DD.MM.YYYY'))
  const numberColumnFormat = { type: 'fixedPoint', precision: 0 }
  const priceColumnFormat = { type: 'fixedPoint', precision: 2 }
  const editorOptions = { step: 0 }

  const getContractDetailAndHeader = async () => {
    try {
      if (props.documentNo) {
        const { data, status } = await getContractDetail({ vbeln: props.documentNo, customerid: props.customerid })
        if (data && status === 200 && data.P_RESULT_CODE === 0) {
          const contractDetail = JSON.parse(data.RESULT[0].data)

          if (contractDetail.rows) {
            for (var i = 0; i < contractDetail.rows.length; i++) {
              contractDetail.rows[i].deleted = false
            }
          }
          setValues(contractDetail)
          const newRows = contractDetail?.rows ? contractDetail.rows.map((rw, index) => ({ ...rw, itemIndex: index + 1 })) : []
          setRows(newRows)
        } else {
          openAlert('error', true, data.P_RESULT_MESSAGE)
        }
      }
    } catch (error) {
      openAlert('error', true, error.message)
    }
  }

  const cbGetContractDetailAndHeader = useCallback(getContractDetailAndHeader, [])

  useEffect(() => { cbGetContractDetailAndHeader() }, [cbGetContractDetailAndHeader])

  const submitContractItems = () => {
    try {
      const tmpRows = []

      const changes = rows.filter(f => f.changedRow === true && f.default_loss_rate > 0)

      for (let i = 0; i < changes.length; i++) {
        const item = changes[i]
        const contractItem = {
          refid: item.refid,
          default_loss_rate: item.default_loss_rate
        }
        tmpRows.push(contractItem)
      }

      // addEfficiencyLossRate({ vbeln: props.documentNo, contractItems: tmpRows }).then(data => {
      //   if (data.P_RESULT_CODE === 0) {
      //     cbGetContractDetailAndHeader()
      //   } else {
      //     openAlert('error', true, data.P_RESULT_MESSAGE)
      //   }
      // })
    } catch (error) {
      openAlert('error', true, error.message)
    }
  }

  const handleUpdate = (row) => {
    const newRow = Object.assign(row.oldData, { ...row.newData, changedRow: true }) // changedRow new
    rows[rows.findIndex(f => f.refid === row.oldData.refid)] = newRow
    setRows(rows)
  }

  const handleRowValidating = (e) => {
    if (e.newData.default_loss_rate < 0) {
      e.isValid = false
      e.errorText = t('enter_positive_value_messsage')
    } else {
      e.isValid = true
      setRows(rows)
    }
  }

  const handleRowPrepared = (rowElement, rowInfo) => {
    if (rowElement.data && rowElement.data.deleted) {
      rowElement.rowElement.style.textDecoration = 'line-through'
    }
  }

  const toolbarBar = () => {
    return (
      <div style={{ width: '400px', height: '8px', paddingTop: '10px' }}>
        <span style={{ position: 'absolute', right: '-25px', top: '5px', fontWeight: '600' }}>{values.contract_percent}%</span>
        <ProgressBar intent='primary' value={values.contract_percent / 100} stripes={false} animate={false} />
      </div>
    )
  }

  return (
    <>
      <div id='panel'>
        <div className='alert alert-success'>
          <div className='row'>
            <div className='col-md-6 col-sm-12'>
              <div className='row'>
                <div className='col-md-3 col-sm-6'><strong>{t('contract_no')}</strong></div>
                <div className='col-md-9 col-sm-6'>: {values.vbeln}</div>
              </div>
              <div className='row'>
                <div className='col-md-3 col-sm-6'><strong>{t('start_date')}</strong></div>
                <div className='col-md-9 col-sm-6'>: {moment(values.guebg).format('DD.MM.YYYY')}</div>
              </div>
            </div>
            <div className='col-md-6 col-sm-12'>
              <div className='row'>
                <div className='col-md-3 col-sm-6'><strong>{t('description')}</strong></div>
                <div className='col-md-9 col-sm-6'>: {values.contdesc}</div>
              </div>
              <div className='row'>
                <div className='col-md-3 col-sm-6'><strong>{t('end_date')}</strong></div>
                <div className='col-md-9 col-sm-6'>: {moment(values.gueen).format('DD.MM.YYYY')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <DataGrid
        ref={refGrid}
        dataSource={rows}
        onToolbarPreparing={onToolbarPreparing}
        allowColumnReordering
        showBorders
        selection={{ mode: 'single' }}
        hoverStateEnabled
        keyExpr='refid'
        showRowLines
        columnAutoWidth
        rowAlternationEnabled
        focusedRowEnabled
        loadPanel={{ enabled: false }}
        onRowUpdating={handleUpdate}
        onRowValidating={handleRowValidating}
        onRowPrepared={handleRowPrepared}
        width='100%'
        onContentReady={(e) => {
          e.component.option('height', document.getElementsByClassName('bp3-dialog')[0].clientHeight - 170)
        }}
        allowColumnResizing
        columnResizingMode='widget'
      >
        <Editing
          mode='cell'
          allowUpdating
          selectTextOnEditStart
        />
        <Export enabled={false} fileName={generateExportFileName} allowExportSelectedData={false} />
        <FilterRow visible />
        <SearchPanel
          width={240}
          placeholder={t('search')}
          visible
          searchVisibleColumnsOnly
        />
        <Paging enabled={false} defaultPageSize={20} />
        <Pager
          showPageSizeSelector
          allowedPageSizes={[20, 50, 100]}
          showNavigationButtons
          showInfo
          infoText={t('pager_info_text')}
          visible={false}
        />
        <Scrolling mode='standart' />
        <Column dataField='itemIndex' caption={t('line')} alignment='center' />
        <Column dataField='vbeln' caption={t('contract_no')} width={100} alignment='center' allowEditing={false} />
        <Column dataField='matnr' caption={t('mat_nr')} width={100} alignment='center' allowEditing={false} />
        <Column dataField='name' caption={t('material')} allowEditing={false} />
        <Column dataField='batch' caption={t('batch')} width={90} allowEditing={false} />
        <Column dataField='expdate' caption={t('exp_date')} width={90} allowEditing={false} />
        <Column dataField='test_amn' caption={t('test_amount')} allowEditing={false} width={110} alignment='center' format={numberColumnFormat} />
        <Column dataField='default_loss_rate' caption={t('efficiency_loss_rate')} width={140} dataType='number' alignment='right' editorOptions={editorOptions} visible={false} cssClass='dx-editable-column' />
        <Column dataField='contractqty' format={numberColumnFormat} caption={t('contract_qty')} width={90} allowEditing={false} />
        <Column dataField='contremqty' format={numberColumnFormat} caption={t('contrem_qty')} width={90} allowEditing={false} />
        <Column dataField='outgoingqty' caption={t('outgoing_qty')} format={numberColumnFormat} width={90} allowEditing={false} />
        <Column
          dataField='miktaryuzdesi'
          customizeText={(cellInfo) => { return cellInfo.value !== null ? cellInfo.value + '%' : '' }}
          caption={t('percent_complete')}
          width={90}
          alignment='right'
          allowEditing={false}
        />
        <Column
          cellRender={({ data }) => (
            <GridCell
              className={data.yuzde_calc === 1 ? 'bp3-grid-icon-arrow-up' : data.yuzde_calc === -1 ? 'bp3-grid-icon-arrow-down' : ''}
              icon={data.yuzde_calc === 1 ? 'arrow-up' : data.yuzde_calc === -1 ? 'arrow-down' : ''}
              // tooltip={t('edit')}
            />)}
          width={36}
          allowSorting={false}
          alignment='center'
          allowReordering={false}
          allowExporting={false}
        />
        <Column dataField='contprice' caption={t('price')} width={90} alignment='right' allowEditing={false} />
        <Column dataField='pricecur' caption={t('curr')} width={70} alignment='center' allowEditing={false} />
        <Column dataField='conttotal' caption={t('contract_total')} alignment='right' width={120} format={priceColumnFormat} allowEditing={false} />
        <Column dataField='remtotal' caption={t('remain_total')} alignment='right' width={120} format={priceColumnFormat} allowEditing={false} />
        <Column dataField='outgoingtotal' caption={t('outgoing_total')} alignment='right' width={120} format={priceColumnFormat} allowEditing={false} />
        <Summary>
          <TotalItem column='itemIndex' summaryType='count' displayFormat={t('count') + ': {0}'} />
          <TotalItem
            column='conttotal'
            summaryType='sum'
            valueFormat={priceColumnFormat}
            displayFormat={'{0}'}
          />
          <TotalItem
            column='remtotal'
            summaryType='sum'
            valueFormat={priceColumnFormat}
            displayFormat={'{0}'}
          />
          <TotalItem
            column='outgoingtotal'
            summaryType='sum'
            valueFormat={priceColumnFormat}
            displayFormat={'{0}'}
          />
        </Summary>
        <Template name='progressBar' render={() => toolbarBar()} />
        <Template name='exportButton' render={() => <ToolbarExportButton onClick={() => { refGrid.current._instance.exportToExcel(false) }} />} />
      </DataGrid>
      {
        <button type='button' style={{ visibility: 'hidden', position: 'absolute' }} ref={ref} onClick={() => { submitContractItems() }}>sendContracts</button>
      }
    </>
  )
})

const onToolbarPreparing = (e) => {
  e.toolbarOptions.items.unshift(
    {
      location: 'before',
      template: 'progressBar'
    },
    {
      location: 'after',
      template: 'exportButton'
    })
}

export default ContractDetail
